import "./style.less";
import { Form, Input } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Paragraph from "antd/lib/typography/Paragraph";
import { useNavigate } from "react-router-dom";

import {
  checkIsValidPassword,
  handleErrorSaveDataResponse,
  IAuthResetPassword,
  IAuthCreatePassword,
  useIsMounted,
} from "../../../@vendor/utils";
import {
  VuiButton,
  VuiFormCard,
  VuiFormItem,
  VuiFormLayout,
  VuiHelmet,
} from "../../../@vendor/components";
import AuthRepository from "../../../repositories/AuthRepository";
import { AxiosError } from "axios";

const AuthCreatePasswordPage = () => {
  const url = new URL(window.location.href);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const token = url.searchParams.get("token");
  const email = url.searchParams.get("email");
  const isInvite = url.searchParams.get("is_invite");
  const [form] = Form.useForm<IAuthResetPassword>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const check = useCallback(async () => {
    setIsLoading(true);
    await AuthRepository.checkToken({
      email: email,
      token: token,
    })
      .then(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      })
      .catch((error: AxiosError) => {
        if (isMounted) {
          setIsLoading(false);
          handleErrorSaveDataResponse(t, error);
          navigate("/auth/login");
        }
      });
  }, [token, email, t]);

  useEffect(() => {
    check();
  }, [check]);

  const onFinish = useCallback(
    async (values: IAuthResetPassword) => {
      setIsLoading(true);

      if (!token || !email) return;

      const payload: IAuthCreatePassword = {
        email: email,
        token: token,
        password: values.password,
        is_invite: Boolean(isInvite),
      };

      try {
        await AuthRepository.createPassword(payload);
        if (isMounted) {
          setIsLoading(false);
          navigate("/auth/create-password/success");
        }
      } catch (error) {
        if (isMounted) {
          setIsLoading(false);
          handleErrorSaveDataResponse(t, error as AxiosError);
        }
      }
    },
    [token, email, t]
  );

  return (
    <div id="auth-create-password-page">
      <VuiHelmet title={t("common.text.createPassword")} />

      <VuiFormLayout form={form} onFinish={onFinish}>
        <VuiFormCard className="auth-create-password-wrapper">
          <h4 className="auth-create-password-title">
            {t("common.text.createNewPassword")}
          </h4>

          <Paragraph style={{ marginBottom: 4 }}>
            {t("auth.createPassword.description")}
          </Paragraph>

          {email ? (
            <Paragraph strong style={{ marginBottom: 24 }}>{email}</Paragraph>
          ) : null}

          <VuiFormItem
            rules={[
              {
                required: true,
                message: t("validation.password"),
              },
              {
                min: 8,
                message: t("validation.password"),
              },
              {
                validator: (_, value) => checkIsValidPassword(t, value),
              },
            ]}
            name="password"
            label={t("common.form.createNewPassword.label")}
          >
            <Input.Password
              placeholder={t("common.form.createNewPassword.placeholder")}
            />
          </VuiFormItem>

          <VuiButton
            loading={isLoading}
            label={t("common.button.submit")}
            buttonProps={{
              htmlType: "submit",
              type: "primary",
            }}
          />
        </VuiFormCard>
      </VuiFormLayout>
    </div>
  );
};

export default AuthCreatePasswordPage;
